import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import getConfig from "next/config";
import { isMobile } from "react-device-detect";
import { OverlayTrigger, Tooltip, Form, Spinner } from "react-bootstrap";

import { hexToRGBA } from "../../../utils/theme";
import { usePrevious } from "../../../hooks";
import xVerifyEmailService, { VALID_EMAIL_STATUSES, INVALID_EMAIL_STATUSES, VALIDATION_MESSAGE } from "../../../../services/xVerifyEmailService";
import { checkEmail } from "../../../../helpers/emailChecker";

const { publicRuntimeConfig } = getConfig();

const StyledGroup = styled(Form.Group)`
  position: relative;

  padding-left: 0;
  padding-right: 0;

  ${({ withLabel }) => withLabel && css`
    padding-top: 24px;
  `};
`;

const StyledLabel = styled(Form.Label)`
  position: absolute;
  left: 0;
  top: 0;
  font-family: 'LatoBold';
  font-size: 12px;
  line-height: 24px;
  text-transform: uppercase;
  white-space: nowrap;
  color: #078FF1;

  span {
    font-size: 16px;
    color: #C61F0C;
  }
`;

const StyledControl = styled(Form.Control)`
  width: 100%;
  min-height: 52px;

  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 16px;
  padding-right: 16px;

  font-family: 'LatoRegular';
  font-size: 14px;
  line-height: 24px;
  color: ${hexToRGBA('#1B3858', 0.5)};
  caret-color: ${hexToRGBA('#1B3858', 0.5)};

  border: 1px solid ${hexToRGBA('#078FF1', 0.1)};
  border-radius: 7px;

  background-color: ${hexToRGBA('#078FF1', 0.1)};

  ${({ isValid }) => isValid && css`
    border: 1px solid #198754 !important;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_success.svg)`} !important;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
    box-shadow: 0 0 0 0.2rem rgb(40 167 69 / 25%) !important;
  `};

  ${({ isInvalid }) => isInvalid && css`
    border: 1px solid #DC3545 !important;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_failure.svg)`} !important;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
    box-shadow: 0 0 0 0.2rem rgb(220 53 69 / 25%) !important;
  `};

  ${({ isLoading }) => isLoading && css`
    border: 1px solid ${hexToRGBA('#078FF1', 0.1)} !important;
    box-shadow: none !important;
    background-image: none !important;
  `};

  &::placeholder {
    color: ${hexToRGBA('#1B3858', 0.5)};
  }
`;

const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: calc((52px - 2rem) / 2);
  right: 16px;

  ${({ withLabel }) => withLabel && css`
    top: calc((52px - 2rem) / 2 + 24px);
  `};

  &.text-custom {
    color: ${hexToRGBA('#078FF1', 1)};
  }
`;

const StyledFeedback = styled(Form.Control.Feedback)`
  &.processing-feedback {
    width: 100%;
    margin-top: 0.25rem;
    font-size: 80%;
    color: #078FF1;
  }
`;

function TextInput({
  id,
  type,
  hint,
  label,
  loading,
  emptyLabel,
  placeholder,
  required,
  pattern,
  maxLength,
  defaultInvalidMessage = '',
  validate,
  isValid,
  isInvalid,
  onKeyPress,
  matchValue,
  isCorpAddress,
  value = '',
  onChange,
  onBlur,
  onKeyDown,
  allowSuccessMessage,
  thirdPartyProps,
  ...restProps
}) {
  const [verificationResult, setVerificationResult] = useState(null);
  const prevValue = usePrevious(value);

  const verifyEmail = useCallback((value) => {
    if (!verificationResult && checkEmail(value)) {
      onChange && onChange({ target: { id: "verifying", value: true } });

      let emailValue = value;
      if (emailValue.indexOf("+") > -1 && emailValue.indexOf("gmail.com") > -1) {
        emailValue =
          emailValue.substring(0, emailValue.indexOf("+")) +
          emailValue.substring(emailValue.indexOf("@"), emailValue.length);
      }
      xVerifyEmailService(emailValue)
        .then(result => {
          setVerificationResult(result);
          onChange && onChange({ target: { id: "verifying", value: false } });
        });
    }
  }, [onChange, verificationResult]);

  const handleChange = useCallback((event) => {
    setVerificationResult(null);
    validate && validate(event, { required, matchValue, isCorpAddress });
    onChange && onChange(event);
  }, [onChange, validate, required, matchValue]);

  const handleBlur = useCallback((event) => {
    if (type === "email" && value) {
      verifyEmail(value);
    }

    onBlur && onBlur(event);
  }, [onBlur, type, value, verifyEmail]);

  const handleKeyDown = useCallback((event) => {
    if (type === "email" && value && event.code === "Enter") {
      if (!verificationResult && checkEmail(value)) {
        onChange && onChange({ target: { id: "suspendedSubmit", value: true } });
      }
      
      verifyEmail(value);
    }

    onKeyDown && onKeyDown(event);
  }, [onKeyDown, onChange, type, value, verificationResult, verifyEmail]);

  const overlayTriggerProps = useMemo(() => {
    const props = {
      placement: isMobile ? "top" : "right-start",
      delay: { show: 0, hide: 0 },
      trigger: ["hover", "focus"],
      overlay: (
        <Tooltip id={`tooltip-${id}`} className="hintTooltip">
          {hint}
        </Tooltip>
      ),
    };

    if (!hint || isMobile) {
      props.show = false;
    }

    return props;
  }, [id, hint]);

  const valid = verificationResult?.status && !loading ? VALID_EMAIL_STATUSES.includes(verificationResult?.status) : isValid;
  const invalid = verificationResult?.status && !loading ? INVALID_EMAIL_STATUSES.includes(verificationResult?.status) : isInvalid;
  const validMessage = VALIDATION_MESSAGE[verificationResult?.reason] || "Verified!";
  const invalidMessage = VALIDATION_MESSAGE[verificationResult?.reason] || defaultInvalidMessage;
  const showInvalidMessage = !(valid && !invalid);
  const withLabel = !!(label || emptyLabel);

  const dynamicProps = useMemo(() => {
    if (thirdPartyProps) {
      if (onKeyPress) {
        return { ...thirdPartyProps, onKeyPress };
      }

      return thirdPartyProps;
    }

    const props = {
      type: type || "text",
      name: id,
      onChange: handleChange,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
    };

    if (onKeyPress) {
      props.onKeyPress = onKeyPress;
    }

    return props;
  }, [thirdPartyProps, id, type, onKeyPress, handleChange, handleBlur]);

  useEffect(() => {
    if (type === "email" && prevValue === undefined && value) {
      verifyEmail(value);
    }
  }, [verifyEmail, type, prevValue, value]);

  return (
    <OverlayTrigger {...overlayTriggerProps}>
      <StyledGroup controlId={id} withLabel={withLabel}>
        <StyledLabel>
          {label}
          {!emptyLabel && label && required && (<span>&nbsp;*</span>)}
        </StyledLabel>
        <StyledControl
          {...dynamicProps}
          {...restProps}
          required={required}
          isValid={valid}
          isInvalid={invalid}
          isLoading={loading}
          placeholder={placeholder}
          pattern={pattern}
          maxLength={maxLength}
          value={value}
          data-hj-allow
        />
        {loading && type === "email" && (
          <StyledFeedback type="processing">
            Verification...
          </StyledFeedback>
        )}
        {allowSuccessMessage && !showInvalidMessage && !loading && (
          <StyledFeedback type="valid">
            {validMessage}
          </StyledFeedback>
        )}
        {showInvalidMessage && !loading && (
          <StyledFeedback type="invalid">
            {invalidMessage}
          </StyledFeedback>
        )}
        {loading && (
          <StyledSpinner
            animation="border"
            variant="custom"
            withLabel={withLabel}
          />
        )}
      </StyledGroup>
    </OverlayTrigger>
  );
}

export default TextInput;
